import React, { useState } from 'react';
import {
  Terminal, Shield, Users, Zap,
  Activity, Settings, ChevronDown, Check, Copy,
  Server, UserPlus, LogOut, UserCog, Lock, RefreshCcw, Layers,
  Download, Globe, Cpu, Apple, Box, ChevronRight, History, Key
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../../components/shared/SEO';

// --- Sub-Component: Interactive Code Snippet ---
const Snippet = ({ cmd, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(cmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-4 py-3 font-mono text-sm group hover:border-orange-500/30 transition-all w-full">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0">
        <span className="text-orange-500 select-none shrink-0 font-bold">$</span>
        <span className="text-gray-300 whitespace-nowrap">{cmd}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {label && <span className="text-[10px] text-gray-600 uppercase font-black tracking-[0.15em] hidden sm:block">{label}</span>}
        <button onClick={handleCopy} className="text-gray-500 hover:text-white transition-colors p-1.5 hover:bg-white/5 rounded-lg">
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
};

// --- Sub-Component: Scenario Builder ---
const CommandStory = ({ title, description, scenarios }) => (
  <div className="mb-10 last:mb-0">
    <h4 className="text-white font-black text-lg mb-2 flex items-center gap-3">
      <div className="w-1 h-5 bg-orange-500 rounded-full" />
      {title}
    </h4>
    <p className="text-gray-500 text-sm mb-5 leading-relaxed max-w-2xl font-medium">
      {description}
    </p>
    <div className="space-y-4">
      {scenarios.map((s, idx) => (
        <div key={idx} className="relative pl-5 border-l border-white/10 hover:border-orange-500/30 transition-colors group">
          <p className="text-[10px] text-orange-500 font-black mb-1.5 uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">
            {s.context}
          </p>
          <Snippet cmd={s.cmd} />
          <p className="text-[11px] text-gray-600 mt-2 font-medium leading-snug">
            <span className="text-gray-400">Result:</span> {s.result}
          </p>
        </div>
      ))}
    </div>
  </div>
);

// --- Main Accordion Section ---
const Chapter = ({ title, icon: Icon, children, defaultOpen = false, subtitle }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className={`border rounded-[2rem] overflow-hidden transition-all duration-500 mb-6 ${isOpen ? 'bg-[#0a0a0a] border-white/10 shadow-2xl shadow-orange-500/5' : 'bg-transparent border-white/5 hover:border-white/10'}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 md:p-8 text-left transition-colors gap-6"
      >
        <div className="flex items-center gap-6">
          <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center transition-all duration-500 shrink-0 ${isOpen ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' : 'bg-white/5 text-gray-500'}`}>
            <Icon size={24} />
          </div>
          <div className="min-w-0">
            <h3 className="text-xl md:text-2xl font-black text-white tracking-tighter truncate">{title}</h3>
            <p className="text-xs md:text-sm text-gray-500 font-medium line-clamp-1">{subtitle}</p>
          </div>
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${isOpen ? 'rotate-180 bg-white/10 text-white' : 'bg-transparent text-gray-700'}`}>
          <ChevronDown size={20} />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-8 md:px-10 md:pb-12 md:ml-16 border-t border-white/5">
              <div className="pt-8">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DashboardDocs() {
  return (
    <>
      <SEO title="Documentation | CLI & API Integration Guide" description="Learn how to initialize the EnvDock CLI, inject secrets into Node.js, and automate secret rotation in your infrastructure." noIndex={true} />

      <div className="max-w-7xl mx-auto pb-24 px-0">

        {/* --- Header Section --- */}
        <div className="mb-16  relative">
          <div className="absolute -top-24 -left-24 w-64 h-64 bg-orange-500/5 blur-[100px] rounded-full pointer-events-none" />
          <h1 className="text-4xl  font-black mb-4 tracking-tighter text-white">
            Developer Journey
          </h1>
          <p className="text-lg text-gray-500 font-medium max-w-2xl leading-relaxed">
            Master the <span className="text-orange-500 font-bold px-1.5 py-0.5 bg-orange-500/10 rounded-md">edk</span> CLI through our comprehensive guide. From local setup to production-grade security automation.
          </p>
        </div>

        {/* CHAPTER 1: THE SETUP */}
        <Chapter
          title="Chapter 1: The Setup"
          subtitle="Installation, binary options, and authentication."
          icon={Download}
          defaultOpen={true}
        >
          <CommandStory
            title="Standard Installation"
            description="For JavaScript/TypeScript developers, the easiest way to keep edk updated is via NPM."
            scenarios={[
              { context: "Global NPM Install", cmd: "npm install -g envdock-cli", result: "Adds the 'edk' command to your system path." },
            ]}
          />

            <CommandStory
            title="Binary Installation (No NPM Required)"
            description="Install EnvDock as a standalone binary. Perfect for minimal Docker images or environments without Node.js."
            scenarios={[
              { context: <div className="flex items-center gap-1"><Terminal size={14} />MacOS</div>, cmd: "brew install envdock-spec/tap/edk", result: "Adds the 'edk' command to your system path." },
              { context: <div className="flex items-center gap-1"><Terminal size={14} />Linux</div>, cmd: "curl -sL https://envdock.cloud/install | bash", result: "Adds the 'edk' command to your system path." },
              { context: <div className="flex items-center gap-1"><Shield size={14} /> Windows (PowerShell)</div>, cmd: "iwr https://envdock.cloud/install.ps1 -useb | iex", result: "Adds the 'edk' command to your system path." },
            ]}
          />

          <div className="h-px bg-white/5 my-10" />

          <CommandStory
            title="Authentication"
            description="Secure your session. Browser-based OAuth is recommended for local development, while email magic links support headless servers."
            scenarios={[
              { context: "Browser Login (Recommended)", cmd: "edk login", result: "Opens the EnvDock dashboard to authenticate your local session." },
              { context: "Headless / Server Login", cmd: "edk login user@example.com", result: "Sends a secure magic link to your email for terminal verification." },
              { context: "Verify Identity", cmd: "edk whoami", result: "Displays the currently authenticated user profile." },
              { context: "Logout", cmd: "edk logout", result: "Removes the token from your local system and end session" },
            ]}
          />

          <div className="h-px bg-white/5 my-10" />

          <CommandStory
            title="Project Initialization"
            description="Link your local workspace/project to your EnvDock cloud project. This creates a hidden configuration file to track environment variables."
            scenarios={[
              { context: "Link Project", cmd: "edk link", result: "Interactive wizard to select your Organization and Project ID." },
              { context: "Check Connection", cmd: "edk status", result: "Verifies the link between your local folder and the cloud vault." }
            ]}
          />
        </Chapter>

        {/* CHAPTER 2: DAILY WORKFLOW */}
        <Chapter
          title="Chapter 2: The Workflow"
          subtitle="Pushing, pulling, and secure injection."
          icon={Activity}
        >
          <CommandStory
            title="Pulling Secrets (Download)"
            description="Sync your local .env with the vault. Secrets are decrypted in-memory before being written to disk."
            scenarios={[
              { context: "Standard Pull (Dev)", cmd: "edk pull", result: "Syncs secrets from the Development environment to your local .env" },
              { context: "Environment Flag", cmd: "edk pull -e prod", result: "Explicitly pulls secrets from the 'Production' environment." },
            ]}
          />

          <div className="h-px bg-white/5 my-10" />

          <CommandStory
            title="Pushing Secrets (Upload)"
            description="Share new variables with your team. This command encrypts your local file before transmission to the cloud."
            scenarios={[
              { context: "Standard Push (Dev)", cmd: "edk push", result: "Uploads local variables to the Development environment." },
              { context: "Environment Flag", cmd: "edk push -e prod", result: "Updates the Production vault with your local configuration." },
            ]}
          />

          <div className="h-px bg-white/5 my-10" />

          <div className="bg-orange-500/5 border border-orange-500/10 rounded-3xl p-6 md:p-8 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 blur-3xl rounded-full" />
            <h4 className="text-orange-500 font-black flex items-center gap-3 mb-3 text-lg relative z-10">
              <Zap size={20} className="shrink-0 fill-orange-500" /> Secret Injection
            </h4>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed font-medium relative z-10">
              Maximize security by bypassing <code className="text-white font-bold">.env</code> files entirely.
              Injection fetches secrets into memory and provides them directly to your app's environment.
            </p>
            <div className="space-y-3 relative z-10">
              <Snippet cmd="edk run -- npm start" label="React.js" result="Injects variables into process.env automatically." />
              <Snippet cmd="edk run -- node server.js" label="Node.js" />
              <Snippet cmd="edk run -e prod -- python app.py" label="Python (Production)" />
            </div>

            {/* Reference Table */}
            <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5 mb-6 mt-3">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-gray-400">
                  <tr>
                    <th className="p-3 font-medium">Framework</th>
                    <th className="p-3 font-medium">Access Syntax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 text-gray-300">
                  <tr>
                    <td className="p-3">Node.js / Backend</td>
                    <td className="p-3 font-mono text-orange-400">process.env.API_URL</td>
                  </tr>
                  <tr>
                    <td className="p-3">React (CRA)</td>
                    <td className="p-3 font-mono text-orange-400">process.env.REACT_APP_API_URL</td>
                  </tr>
                  <tr>
                    <td className="p-3">Vite (Vue/React)</td>
                    <td className="p-3 font-mono text-orange-400">import.meta.env.VITE_API_URL</td>
                  </tr>
                  <tr>
                    <td className="p-3">Next.js (Client)</td>
                    <td className="p-3 font-mono text-orange-400">process.env.NEXT_PUBLIC_API_URL</td>
                  </tr>
                  <tr>
                    <td className="p-3">Python / Go</td>
                    <td className="p-3 font-mono text-orange-400">os.environ["API_URL"]</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Magic Feature Note */}
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
              <div className="flex gap-2 text-orange-200 text-sm">
                <span className="text-lg">✨</span>
                <p>
                  <span className="font-bold text-white">Magic Feature:</span> You don't need to rename secrets! Just name it <code className="bg-black/30 px-1 py-0.5 rounded text-orange-400">API_URL</code> in EnvDock, and we auto-generate the prefixed versions (REACT_APP_, VITE_, etc.) instantly.
                </p>
              </div>
            </div>


          </div>
        </Chapter>

        {/* CHAPTER 3: SAFETY */}
        <Chapter
          title="Chapter 3: Safety Nets"
          subtitle="Audit logs, versioning, and disaster recovery."
          icon={Shield}
        >
          <CommandStory
            title="Version Control & Rollbacks"
            description="Every secret change is immutable and versioned. If a deployment fails due to a bad config, roll back to a known-stable state instantly."
            scenarios={[
              { context: "View Audit Log", cmd: "edk history", result: "A chronological record of all changes, timestamps, and actors." },
              { context: "List Versions", cmd: "edk versions ls", result: "Displays a table of historical snapshots for the current environment." },
              { context: "List Production Versions", cmd: "edk versions ls -e prod", result: "Filters version history specifically for Production." },
              { context: "Rollback (Interactive)", cmd: "edk versions rollback", result: "Prompts selection of a previous version to restore as active." },
              { context: "Production Rollback", cmd: "edk versions rollback -e prod", result: "Restores a previous Production state immediately." }
            ]}
          />
        </Chapter>

        {/* CHAPTER 4: TEAM */}
        <Chapter
          title="Chapter 4: Team & Robots"
          subtitle="Collaborators and CI/CD service tokens."
          icon={Users}
        >
          <div className="space-y-12">

            <div>
              <h4 className="text-white font-black text-xl mb-3 flex items-center gap-3">
                <UserPlus size={20} className="text-orange-500" /> Humans
              </h4>
              <p className="text-gray-500 text-sm mb-6 font-medium">
                Grant team members access to specific project vaults. Requires Admin privileges.
              </p>
              <div className="space-y-4 pl-5 border-l border-white/5">
                <Snippet cmd="edk team ls" label="List Access" />
                <Snippet cmd="edk team add dev@corp.com" label="Add Member" />
                <p className="text-[11px] text-gray-600 font-bold uppercase tracking-widest pl-2 italic">Select Role: Viewer, Editor, or Admin</p>

                <Snippet cmd="edk team update dev@corp.com" label="Modify Role" />
                <Snippet cmd="edk team remove dev@corp.com" label="Revoke Access" />
              </div>
            </div>


            <div className="bg-white/[0.02] border border-white/5 rounded-[2rem] p-8">
              <h4 className="text-white font-black text-xl mb-3 flex items-center gap-3">
                <Server size={20} className="text-orange-500" /> Robots (CI/CD)
              </h4>
              <p className="text-gray-500 text-sm mb-6 font-medium">
                Automate deployments using Service Tokens. Avoid using personal accounts in GitHub Actions or AWS.
              </p>
              <div className="space-y-4">
                <Snippet cmd="edk tokens create ci-deploy" label="Generate Token" />
                <div className="bg-orange-500/5 p-4 rounded-xl text-[11px] text-orange-400 border border-orange-500/10 font-bold uppercase tracking-widest">
                  Crucial: Tokens are only visible once. Store them in your CI provider immediately.
                </div>
                <Snippet cmd="edk tokens ls" label="Active Tokens" />
                <Snippet cmd="edk tokens revoke <id>" label="Kill Token" />
              </div>
            </div>
          </div>
          <div className="space-y-12 mt-8">
          </div>
        </Chapter>

        {/* ... Previous Chapters ... */}

        <Chapter
          title="Chapter 5: API & Endpoints"
          subtitle="Programmatic access via HTTP."
          icon={Globe}
        >
          {/* Authentication Section */}
          <div>
            <h4 className="text-white font-black text-xl mb-3 flex items-center gap-3">
              <Key size={20} className="text-orange-500" /> Authentication
            </h4>
            <p className="text-gray-500 text-sm mb-6 font-medium">
              All requests must include your Service Token in the header.
              <br />Base URL: <span className="text-orange-400 font-mono">https://envdock.cloud/api/cli</span>
            </p>

            <div className="bg-[#090909] border border-white/10 rounded-xl p-4 overflow-hidden">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2 uppercase tracking-widest font-bold">
                <span>Header</span>
                <span>Value</span>
              </div>
              <div className="font-mono text-sm text-white flex gap-4">
                <span className="text-purple-400">x-service-token</span>
                <span>edk_live_...</span>
              </div>
            </div>
          </div>

          {/* Endpoints Documentation */}
          <div className="space-y-8 bg-white/[0.02] border border-white/5 rounded-[2rem] p-8 mt-8">

            {/* 1. GET Secrets */}
            <div className="relative group">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-1 bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-widest rounded border border-green-500/20">GET</span>
                <code className="text-sm text-white font-mono">/:projectId</code>
              </div>
              <p className="text-gray-500 text-sm mb-3">Fetch all decrypted secrets for a specific environment.</p>

              <div className="bg-[#111] rounded-xl border border-white/5 p-4 text-xs font-mono text-gray-400">
                <span className="text-gray-500 block mb-2"># Query Parameters</span>
                <span className="text-orange-400">?env</span>=<span className="text-green-300">production</span>

                <div className="mt-4 pt-4 border-t border-white/5">
                  <span className="text-gray-500 block mb-2"># Response (JSON)</span>
                  <span className="text-white">{"{"} "API_KEY": "sk_live_...", "DB_HOST": "10.0.0.1" {"}"}</span>
                </div>
              </div>
            </div>

            {/* 2. PUSH Secrets */}
            <div className="relative group">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-black uppercase tracking-widest rounded border border-blue-500/20">POST</span>
                <code className="text-sm text-white font-mono">/push/:projectId</code>
              </div>
              <p className="text-gray-500 text-sm mb-3">Bulk upload secrets. Triggers a new version and integration sync.</p>

              <div className="bg-[#111] rounded-xl border border-white/5 p-4 text-xs font-mono text-gray-400">
                <span className="text-gray-500 block mb-2"># Body (JSON)</span>
                <pre className="whitespace-pre-wrap text-green-300">
                  {`{
  "env": "production",
  "secrets": {
    "NEW_VAR": "value123",
    "ANOTHER_VAR": "true"
  }
}`}
                </pre>
              </div>
            </div>

            {/* 3. ROLLBACK */}
            <div className="relative group">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-1 bg-orange-500/10 text-orange-400 text-[10px] font-black uppercase tracking-widest rounded border border-orange-500/20">POST</span>
                <code className="text-sm text-white font-mono">/:projectId/rollback</code>
              </div>
              <p className="text-gray-500 text-sm mb-3">Revert environment to a previous snapshot immediately.</p>

              <div className="bg-[#111] rounded-xl border border-white/5 p-4 text-xs font-mono text-gray-400">
                <span className="text-gray-500 block mb-2"># Body (JSON)</span>
                <pre className="whitespace-pre-wrap text-green-300">
                  {`{
  "env": "production",
  "version": 14
}`}
                </pre>
              </div>
            </div>

            {/* 4. HISTORY */}
            <div className="relative group">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-1 bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-widest rounded border border-green-500/20">GET</span>
                <code className="text-sm text-white font-mono">/:projectId/versions</code>
              </div>
              <p className="text-gray-500 text-sm mb-3">Get a list of past versions for audit trails.</p>

              <div className="bg-[#111] rounded-xl border border-white/5 p-4 text-xs font-mono text-gray-400">
                <span className="text-gray-500 block mb-2"># Query Parameters</span>
                <span className="text-orange-400">?env</span>=<span className="text-green-300">production</span>
              </div>
            </div>

          </div>
        </Chapter>

        {/* CHAPTER 6: CONFIGURATION */}
        <Chapter
          title="Chapter 6: Configuration"
          subtitle="Wizard menu and global environment settings."
          icon={Settings}
        >
          <div className="mb-10">
            <h4 className="text-white font-black text-lg mb-2 flex items-center gap-3">
              <Settings size={20} className="text-orange-500" /> Interactive Management
            </h4>
            <p className="text-gray-500 text-sm mb-6 font-medium leading-relaxed">
              Launch the visual configuration wizard to manage your identity and CLI preferences without flags.
            </p>
            <Snippet cmd="edk config" label="Open Wizard" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-10">
            <div className="space-y-5 p-6 bg-white/[0.02] border border-white/5 rounded-3xl">
              <h5 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                <UserCog size={16} className="text-orange-500" /> Identity Settings
              </h5>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-500 font-bold">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" /> Update Display Name
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 font-bold">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" /> Rotate Password
                </div>
              </div>
            </div>

            <div className="space-y-5 p-6 bg-white/[0.02] border border-white/5 rounded-3xl">
              <h5 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 flex items-center gap-2">
                <Layers size={16} className="text-orange-500" /> CLI Preferences
              </h5>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-500 font-bold">
                  <div className="w-1.5 h-1.5 bg-orange-500 rounded-full" /> Toggle Default Environment
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 font-bold">
                  <div className="w-1.5 h-1.5 bg-red-600 rounded-full" /> Wipe Local Configuration
                </div>
              </div>
            </div>
          </div>
        </Chapter>

        {/* Footer Navigation */}
        <div className="mt-16 pt-10 border-t border-white/5 flex justify-center">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-700">
            End of Documentation — © 2026 EnvDock Systems
          </p>
        </div>

      </div>
    </>
  );
}